import React, { useEffect, useState, useCallback, useMemo } from "react";
import { fetchUpdateProfile, fetchUpdateProfilePic } from "../../Reducers/actions/userActions";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "../../Reducers/actions/authActions";
import { useNavigate } from "react-router-dom";
import { CompleteProfileV2Skeleton } from "../../components/Skeletons/BatchScreenSkeletons";
import {
    completeOnboarding,
    getConnectCountries,
    getKycStatus,
} from "../../Reducers/actions/accountActions";
import { useStripeConnectEmbeddedOnboarding } from "../../hooks/useStripeConnectEmbeddedOnboarding";
import StripeEmbeddedOnboarding from "../../components/StripeEmbeddedOnboarding/StripeEmbeddedOnboarding";
import { getShippingAddresses } from "../../Reducers/actions/shippingAddressActions";
import { selectMainShippingAddress } from "../../Reducers/actions/shippingAddressUpsert";
import { getCountriesAndRegions } from "../../Reducers/actions/shippingMethodsActions";
import {
    ONBOARDING_COUNTRY_REQUIRED_MESSAGE,
    ONBOARDING_UNSUPPORTED_COUNTRY_MESSAGE,
    getCompleteProfileStepConfig,
    getOnboardingErrorMessage,
    getRegionFromLocale,
    orderCountriesWithTopUsed,
} from "./completeProfileOnboarding";
import {
    getStripeConnectCountryCode,
    isSupportedStripeConnectCountry,
} from "../../constants/stripeConnectCountries";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import "./AuthCommLink.scss"
import Icon from "../../components/Icon/Icon";
import ProfilePicture from "../../components/ProfilePicture/ProfilePicture";
import { SIGNUP_CREATOR_SESSION_KEY, VERIFY_EMAIL_BANNER_EMAIL_KEY } from "../../constants/signupFlow";
import ExpiredLinkResend from "./ExpiredLinkResend";

const expiredLinkHash = new URLSearchParams(window.location.hash.slice(1));
const isExpiredLink =
  expiredLinkHash.get("error_code") === "otp_expired" ||
  expiredLinkHash.get("error") === "access_denied";

const CompleteProfileV2 = () => {
    const user = useSelector((state) => state.auth?.user || {})
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [step, setStep] = useState(0)
    const [changed, setChanged] = useState(false);
    const { connectInstance, initializeEmbeddedOnboarding, handleEmbeddedOnboardingExit: exitEmbeddedOnboarding } =
        useStripeConnectEmbeddedOnboarding();
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [verificationError, setVerificationError] = useState(null);
    const [lightKycReady, setLightKycReady] = useState(false);
    const [stripeAccountId, setStripeAccountId] = useState(null);
    const [kycChecking, setKycChecking] = useState(false);
    const [countrySelection, setCountrySelection] = useState('');

    const [updatableFields, setUpdatableFields] = useState({
        bio: user?.bio || '',
        address: user?.address || '',
        email: user?.email || '',
        fullName: `${user?.firstName ? user.firstName + " " : ""}${user.lastName || ""}`,
    });
    // null = not loaded yet — validation falls back to the seeded static list.
    const [supportedCountryCodes, setSupportedCountryCodes] = useState(null);
    const [loading, setLoading] = useState(true)
    const [savingProfile, setSavingProfile] = useState(false);
    const shippingAddresses = useSelector((state) => state?.shippingAddress?.addresses || []);
    const countriesRegions = useSelector((state) => state?.shippingMethods?.countriesRegions || null);
    const isCreator = useMemo(() => {
        if (sessionStorage.getItem(SIGNUP_CREATOR_SESSION_KEY) === "1") return true;
        return user?.role === "creator";
    }, [user?.role]);
    const ENABLE_STRIPE_PROFILE_STEP = true;
    const hasStripeStep = ENABLE_STRIPE_PROFILE_STEP && isCreator;
    const { stripeStep, finalStep, titles: currentTitles } =
        getCompleteProfileStepConfig(hasStripeStep);

    const allCountries = useMemo(() => {
        const list = countriesRegions?.allCountries || [];
        const byCode = new Map();
        list.forEach((country) => {
            const code = String(country.code || country.country_code || "").toUpperCase();
            const name = country.name || country.country_name || code;
            if (code && !byCode.has(code)) byCode.set(code, { code, name });
        });
        return orderCountriesWithTopUsed([...byCode.values()]);
    }, [countriesRegions]);
    const countryOptions = useMemo(
        () => allCountries.map((country) => ({ value: country.code, label: country.name })),
        [allCountries]
    );
    const isCountrySupported = useCallback(
        (code) => {
            if (!code) return false;
            if (supportedCountryCodes) {
                return supportedCountryCodes.has(String(code).trim().toUpperCase());
            }
            return isSupportedStripeConnectCountry(code);
        },
        [supportedCountryCodes]
    );

    useEffect(() => {
        if (user && user?.bio?.length > 0 && user?.address?.length > 0) {
            navigate("/explore")
        } else {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (user && !loading) {
            // Only update if the fields haven't been set yet or if user data changed
            setUpdatableFields(prev => {
                const newFullName = `${user?.firstName ? user.firstName + " " : ""}${user.lastName || ""}`;

                // Check if we need to update
                if (prev.bio === (user?.bio || '') &&
                    prev.address === (user?.address || '') &&
                    prev.email === (user?.email || '') &&
                    prev.fullName === newFullName) {
                    return prev; // No changes needed
                }

                return {
                    ...prev,
                    bio: user?.bio || '',
                    address: user?.address || '',
                    email: user?.email || '',
                    fullName: newFullName,
                };
            });
        }
    }, [user.bio, user.address, user.firstName, user.lastName, loading])

    const onImageUpload = (obj) => {
        fetchUpdateProfilePic(obj, user.user_id).then(({ success, data }) => {
            if (success) {
                dispatch(updateUser({ ...user, profilePicture: data }));
                localStorage.setItem("pdp", data);
            }
        });
    };

    const handleUpdateProfile = () => {
        return new Promise((resolve) => {
            if (updatableFields.bio.length > 0 || updatableFields.fullName.length > 0) {
                setSavingProfile(true);
            const obj = {
                ...updatableFields.bio?.length && { bio: updatableFields.bio },
                ...updatableFields.fullName?.length && {
                    firstName: updatableFields.fullName.split(' ')[0],
                    lastName: updatableFields.fullName.split(' ')[1]
                },
            }
            fetchUpdateProfile(obj, user.user_id).then(({ success, message, user: resUser }) => {
                if (success) {
                    dispatch(updateUser({ ...user, ...resUser }))
                    resolve(true)
                } else {
                    console.log(message)
                    resolve(false)
                }
            }).finally(() => setSavingProfile(false))
            } else {
                resolve(true);
            }
        });
    }

    const handleChange = useCallback((key, value) => {
        if (!changed) setChanged(true)
        setUpdatableFields(prev => ({ ...prev, [key]: value }))
    }, [changed])

    // Supported Connect countries from the backend (Stripe country specs);
    // the static seed list stays as fallback while loading / on failure.
    useEffect(() => {
        if (!hasStripeStep) return;
        getConnectCountries()
            .then((response) => {
                const codes = response?.data?.countries
                    ?.map((country) => String(country?.code || "").toUpperCase())
                    .filter(Boolean);
                if (Array.isArray(codes) && codes.length) {
                    setSupportedCountryCodes(new Set(codes));
                }
            })
            .catch(() => { /* fall back to the seeded list */ });
    }, [hasStripeStep]);

    // Pre-select the payout country: saved main address first, then the
    // browser locale. The user always confirms before an account is created.
    useEffect(() => {
        if (!hasStripeStep) return;
        setCountrySelection((prev) => {
            if (prev) return prev;
            const mainAddress = selectMainShippingAddress(shippingAddresses);
            const fromAddress = getStripeConnectCountryCode(mainAddress?.country || '', allCountries);
            if (fromAddress) return fromAddress;
            const fromLocale = getRegionFromLocale(
                typeof navigator !== "undefined" ? navigator.language : ''
            );
            if (fromLocale && allCountries.some((country) => country.code === fromLocale)) {
                return fromLocale;
            }
            return prev;
        });
    }, [hasStripeStep, shippingAddresses, allCountries]);

    const handleFinish = () => {
        sessionStorage.removeItem(SIGNUP_CREATOR_SESSION_KEY);
        navigate("/on-boarding");
    };

    const syncLightKycStatus = async () => {
        const response = await getKycStatus();
        if (!response?.success || !response?.data) {
            return null;
        }

        const hasLightKyc = response.data.has_light_kyc === true;
        setLightKycReady(hasLightKyc);
        setStripeAccountId(response.data.account_id || null);
        return response.data;
    };

    const handleEmbeddedOnboardingExit = async () => {
        try {
            const status = await exitEmbeddedOnboarding();
            setLightKycReady(status.has_light_kyc === true);
            setStripeAccountId(status.account_id);
            setVerificationError(null);
        } catch (error) {
            console.error("Failed to refresh KYC after onboarding:", error);
            setVerificationError("Unable to refresh verification status right now.");
        }
    };

    const startVerification = async () => {
        setVerificationError(null);
        setVerificationLoading(true);
        try {
            let accountId = stripeAccountId;
            if (!accountId) {
                const latestStatus = await syncLightKycStatus();
                if (latestStatus?.has_light_kyc === true) return;
                accountId = latestStatus?.account_id || null;
            }

            if (!accountId) {
                // The Stripe account country is immutable — it is only ever
                // created here, from the country the user just confirmed.
                if (!countrySelection) {
                    setVerificationError(ONBOARDING_COUNTRY_REQUIRED_MESSAGE);
                    return;
                }
                if (!isCountrySupported(countrySelection)) {
                    setVerificationError(ONBOARDING_UNSUPPORTED_COUNTRY_MESSAGE);
                    return;
                }

                const onboardingResponse = await completeOnboarding({ country: countrySelection });
                if (!onboardingResponse?.success || !onboardingResponse?.data?.account_id) {
                    setVerificationError(getOnboardingErrorMessage(onboardingResponse));
                    return;
                }
                accountId = onboardingResponse.data.account_id;
                setStripeAccountId(accountId);
            }

            await initializeEmbeddedOnboarding(accountId);
        } catch (error) {
            console.error("Failed to start light KYC onboarding:", error);
            setVerificationError(getOnboardingErrorMessage(error));
        } finally {
            setVerificationLoading(false);
        }
    };

    // Entering the payout step: load the countries list, check the current
    // KYC state, and resume a started verification without an extra tap.
    useEffect(() => {
        if (step !== stripeStep) return;
        if (!countriesRegions) dispatch(getCountriesAndRegions());
        if (!shippingAddresses.length) dispatch(getShippingAddresses());

        let cancelled = false;
        (async () => {
            setKycChecking(true);
            setVerificationError(null);
            try {
                const status = await syncLightKycStatus();
                if (cancelled) return;
                if (status?.account_id && status?.has_light_kyc !== true) {
                    await initializeEmbeddedOnboarding(status.account_id);
                }
            } catch {
                if (!cancelled) {
                    setVerificationError("Unable to verify account status right now.");
                }
            } finally {
                if (!cancelled) setKycChecking(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const handleChangeStep = async () => {
        if (step < 2) {
            setStep((prev) => prev + 1);
            return;
        }
        if (step === 2) {
            const saved = await handleUpdateProfile();
            if (!saved) return;
            if (stripeStep) {
                setStep(stripeStep);
                return;
            }
            handleFinish();
            return;
        }
        if (step === stripeStep) {
            if (lightKycReady) {
                handleFinish();
                return;
            }
            await startVerification();
        }
    }


    const handleSkip = async () => {
        if (step < 2) {
            setStep(prev => prev + 1);
            return;
        }
        if (step === 2) {
            const saved = await handleUpdateProfile();
            if (!saved) return;
            if (stripeStep) {
                setStep(stripeStep);
                return;
            }
        }
        handleFinish();
    }
    const pdp = localStorage.getItem("pdp");

    if (isExpiredLink) {
        let savedEmail = null;
        try { savedEmail = localStorage.getItem(VERIFY_EMAIL_BANNER_EMAIL_KEY); } catch { /* ignore */ }
        window.history.replaceState(null, "", window.location.pathname);
        return <ExpiredLinkResend initialEmail={savedEmail} />;
    }

    if (loading) return <CompleteProfileV2Skeleton />

    // The embedded Stripe form has its own confirm button — hide the fixed
    // footer while it's open so nothing covers it.
    const embeddedFormOpen = step === stripeStep && !!connectInstance && !lightKycReady;
    const footerLabel = step === stripeStep
        ? (lightKycReady ? "LET'S HYK" : verificationLoading ? "STARTING..." : "CONTINUE")
        : step === finalStep ? "LET'S HYK" : "CONTINUE";

    return (
        <div className="d-flex f-column aic bg-red p-fixed" style={{ height: `100%`, width: '100dvw', top: 0, left: 0, overflow: 'scroll' }}>
            {user ? <div className="complete-profile-container pt-40 mt-40 auth-bg">
                {step > 0 && <span className="p-absolute back-icon" onClick={() => setStep(prev => prev - 1)}>
                    <Icon alias={"chevron-left"} width={24} color="#000" className="ml-16" />
                </span>}
                <p className="fs-sm2 fw-bold mb-32 mt-12 ta-center">{currentTitles[step]}</p>
                {/* Hide SKIP only on the creator Stripe step, where the primary
                    "LET'S HYK" button already finishes onboarding (so SKIP is
                    redundant). Non-creators finish on the address step, where
                    SKIP still lets them bypass the address — keep it for them. */}
                {!(hasStripeStep && step === finalStep) && (
                    <p className="p-absolute skip fg-red td-underline fs-xxs fw-bold mt-0" onClick={() => handleSkip()}>SKIP</p>
                )}

                <div className="d-flex aic jcc p-16 body-complete">
                    {step === 0 &&
                        <input
                            className="input-main w-full mt-12"
                            type="text"
                            placeholder="Full Name"
                            value={updatableFields.fullName}
                            onChange={(e) => handleChange("fullName", e.target.value)}
                            onFocus={() => {
                                // Scroll to top when input is focused
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="words"
                            spellCheck="false"
                            data-form-type="other"
                            data-lpignore="true"
                            data-1p-ignore="true"
                        />
                    }

                    {step === 1 &&
                        <ProfilePicture
                            imgUrl={!pdp || pdp === 'null' || pdp.length === 0 ? user?.profilePicture : pdp}
                            onImageUpload={onImageUpload}
                            showIcon={true}
                            classNames={'mb-12 mt-40'}
                        />
                    }

                    {step === 2 &&
                        <div className="p-relative w-full">
                            <textarea
                                className="input-main w-full mt-12"
                                placeholder="Bio"
                                value={updatableFields.bio}
                                onChange={(e) => handleChange("bio", e.target.value)}
                                onFocus={() => {
                                    // Scroll to top when input is focused
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                maxLength={1000}
                                rows={8}
                                style={{ height: '120px' }}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="sentences"
                                spellCheck="false"
                                data-form-type="other"
                                data-lpignore="true"
                                data-1p-ignore="true"
                            />
                            <span className="p-absolute fs-tiny fw-medium fg-grey" style={{ right: '12px', bottom: '12px' }}>1000</span>
                        </div>
                    }

                    {step === stripeStep && (
                        <div className="w-full">
                            {lightKycReady ? (
                                <div className="ta-center pt-24">
                                    <Icon alias="check_v2" width={48} color="#1a7f37" />
                                    <p className="fs-regular fw-bold mt-16 mb-0">Verification completed</p>
                                    <p className="fs-tiny fg-hyk-medgrey mt-8 mb-0">
                                        You&apos;re all set to receive payouts.
                                    </p>
                                </div>
                            ) : connectInstance ? (
                                <div className="stripe-embedded-onboarding">
                                    <StripeEmbeddedOnboarding
                                        connectInstance={connectInstance}
                                        onExit={handleEmbeddedOnboardingExit}
                                    />
                                </div>
                            ) : kycChecking ? (
                                <p className="fs-tiny fg-hyk-medgrey ta-center mt-24 mb-0">
                                    Checking verification status...
                                </p>
                            ) : (
                                <>
                                    <p className="fs-regular fg-hyk-medgrey ta-center mt-0 mb-16 px-8">
                                        Set up payouts so you can receive earnings from your creations. Secured by Stripe.
                                    </p>
                                    <p className="creator-stripe-wordmark ta-center mt-0 mb-24">stripe</p>
                                    <CustomSelect
                                        options={countryOptions}
                                        value={countrySelection || ''}
                                        onChange={(value) => {
                                            setVerificationError(null);
                                            setCountrySelection(value);
                                        }}
                                        placeholder={countryOptions.length ? "Country" : "Loading countries..."}
                                        searchable
                                        searchPlaceholder="Search countries"
                                        name="country"
                                        loading={!countryOptions.length}
                                    />
                                    {countrySelection && !isCountrySupported(countrySelection) && (
                                        <p className="fs-tiny fw-medium fg-xtred mt-8 mb-0">
                                            {ONBOARDING_UNSUPPORTED_COUNTRY_MESSAGE}
                                        </p>
                                    )}
                                    <p className="fs-tiny fg-hyk-medgrey mt-12 mb-0">
                                        Your country determines how you get paid — it can&apos;t be
                                        changed later. Stripe will guide you through the rest.
                                    </p>
                                </>
                            )}
                            {verificationError && (
                                <p className="fs-tiny fw-medium fg-xtred ta-center mt-12 mb-0">
                                    {verificationError}
                                </p>
                            )}
                        </div>
                    )}

                    {!embeddedFormOpen && (
                        <div className='p-fixed bg-white auth-footer pb-24 mt-24'>
                            <button
                                type="submit"
                                className="btn red-btn btn-full-v d-flex aic jcc p-relative"
                                onClick={handleChangeStep}
                                disabled={
                                    loading ||
                                    savingProfile ||
                                    verificationLoading ||
                                    (step === stripeStep && kycChecking)
                                }
                            >
                                {footerLabel}
                            </button>
                        </div>
                    )}
                </div>
            </div> : <CompleteProfileV2Skeleton />}
        </div>
    )
}

export default CompleteProfileV2
